import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';

import { AccessRestrictionMode, PeoStaffRole, QueryMode } from 'src/api';
import { FieldArrayTemplate } from 'src/components/common';
import { SelectField, TextField } from 'src/components/finalForm';
import { useEmployeeBlacklistAutocomplete } from 'src/hooks/employee';
import { useEmployersAutocomplete } from 'src/hooks/employer';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { SelectFieldOption } from 'src/types/components';

import { AccessRestrictionModeFormPart } from '../AccessRestrictionModeFormPart';

import { InviteFormFields, InviteFormFieldsForStaff } from './constants';
import { InviteFormFieldsValues, InviteFormFieldsValuesForStaff } from './types';
import { inviteFormValidationSchema } from './utils/validation';

interface InvitePeoStaffFormProps {
  peoId: string;
  roleFieldSelectOptions: SelectFieldOption<PeoStaffRole>[];
  onClose: () => void;
  onSubmit: (formFields: InviteFormFieldsValues) => void;
  loading?: boolean;
  isPartnership?: boolean;
}

export const InvitePeoStaffForm: React.FC<InvitePeoStaffFormProps> = ({
  peoId,
  roleFieldSelectOptions,
  onClose,
  onSubmit,
  loading,
  isPartnership,
}) => {
  const { hasPlatformRoles, isPeoPayrollProviderUser, isPeoSuperAdmin } =
    useCurrentUserPermissions();

  const canSetupAccessRestriction = hasPlatformRoles || isPeoPayrollProviderUser || isPeoSuperAdmin;

  const employersSelectProps = useEmployersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
    queryOptions: {
      variables: {
        filter: { peoId: { equals: peoId } },
      },
      skip: !canSetupAccessRestriction,
    },
  });

  const employeesSelectProps = useEmployeeBlacklistAutocomplete({
    peoId,
    skip: !canSetupAccessRestriction,
  });

  const emptyStaffObject: InviteFormFieldsValuesForStaff = {
    email: '',
    accessRestrictionMode: AccessRestrictionMode.Blacklist,
    businessRole: roleFieldSelectOptions[0]?.value || PeoStaffRole.PeoAdmin,
    employerBlacklist: [],
    employeeBlacklist: [],
    employerWhitelist: [],
  };

  const initialValues: InviteFormFieldsValues = {
    [InviteFormFields.staff]: [emptyStaffObject],
  };

  return (
    <React.Fragment>
      <Typography color="GrayText" variant="body1" component="h4" mb={3} lineHeight="24px">
        Invite Staff by using their email address
      </Typography>

      <Form<InviteFormFieldsValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        subscription={{ submitting: true, valid: true, error: true }}
        initialValuesEqual={() => true}
        containerProps={{ display: 'flex', flexDirection: 'column' }}
        validationSchema={inviteFormValidationSchema}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <FieldArrayTemplate
                label="Invitation"
                name={InviteFormFields.staff}
                addIcon={<AddIcon />}
                onAdd={fields => fields.push(emptyStaffObject)}
                onRemove={(fields, index) => fields.remove(index)}
              >
                {({ renderFieldName, index }) => (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Field
                      name={renderFieldName(InviteFormFieldsForStaff.email)}
                      component={TextField}
                      label="Email Address"
                      fullWidth
                      disabled={submitting || loading}
                      required
                    />

                    <Field
                      name={renderFieldName(InviteFormFieldsForStaff.businessRole)}
                      component={SelectField}
                      label="Role"
                      options={roleFieldSelectOptions}
                      fullWidth
                      disabled={submitting || loading || roleFieldSelectOptions.length === 1}
                      required
                    />

                    <FormSpy<InviteFormFieldsValues> subscription={{ values: true }}>
                      {({ values }) => {
                        const peoStaffValue = values.staff[index];
                        const isSuperPeoAdmin =
                          peoStaffValue?.businessRole === PeoStaffRole.PeoSuperAdmin;
                        if (!canSetupAccessRestriction || isSuperPeoAdmin) return null;

                        const selectedAccessRestrictionMode =
                          peoStaffValue?.accessRestrictionMode ||
                          emptyStaffObject.accessRestrictionMode;

                        return (
                          <AccessRestrictionModeFormPart
                            renderFieldName={renderFieldName}
                            selectedAccessRestrictionMode={selectedAccessRestrictionMode}
                            employersSelectProps={employersSelectProps}
                            employeesSelectProps={employeesSelectProps}
                            employersOnly={isPartnership}
                          />
                        );
                      }}
                    </FormSpy>
                  </Box>
                )}
              </FieldArrayTemplate>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  type="reset"
                  color="error"
                  variant="outlined"
                  onClick={onClose}
                  disabled={submitting || loading}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || loading || submitting}
                  loading={submitting || loading}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};
